import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Table, Space, Popconfirm, Switch, Tooltip, Tag, message } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, SyncOutlined, SaveOutlined, CloseCircleOutlined, CheckOutlined } from "@ant-design/icons";
import axios from "axios";
import appConfig from "../../../utils/config";
import {
    getCourseFaqs,
    createCourseFaq,
    updateCourseFaq,
    deleteCourseFaq,
} from "../../../appRedux/actions/courses";
import CourseFAQForm from "./CourseFAQForm";
import AIGenerateAction from "../../Common/AIGenerateAction";

const CourseFAQList = ({ courseId }) => {
    const dispatch = useDispatch();
    const { courseFaqs, courseInfo } = useSelector((state) => state.courses);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingFaq, setEditingFaq] = useState(null);
    const [draftFaqs, setDraftFaqs] = useState([]); // Local state for generated but unsaved FAQs
    const [regeneratingId, setRegeneratingId] = useState(null); // Track which draft is spinning
    
    // Auth token is stored as accessToken in redux authReducer
    const { accessToken, user } = useSelector((state) => state.auth);
    const token = accessToken || localStorage.getItem("accessToken");


    // Use Hex ID for all operations as per API spec
    const effectiveId = courseInfo?._id || courseId;

    // Helper to check for valid Mongo ObjectId
    const isHexId = (id) => /^[0-9a-fA-F]{24}$/.test(id);

    // Compute live/hidden counts (excluding drafts)
    const liveCount = courseFaqs.filter((faq) => faq.is_active !== false).length;
    const hiddenCount = courseFaqs.length - liveCount;

    useEffect(() => {
        console.log("CourseFAQList mounted/updated. effectiveId:", effectiveId);
        if (effectiveId && isHexId(effectiveId)) {
            console.log("Valid Hex ID found in CourseFAQList. Fetching FAQs for:", effectiveId);
            dispatch(getCourseFaqs(effectiveId)).catch(() => {
                // Error is already handled in Redux action with userSignOut
                console.log("FAQ fetch failed (likely session expired)");
            });
        } else {
            console.log("effectiveId is not a valid Hex ID (yet). Skipping fetch. Value:", effectiveId);
        }
    }, [dispatch, effectiveId]);

    const handleAddFaq = () => {
        setEditingFaq(null);
        setIsModalVisible(true);
    };

    const handleEditFaq = (faq) => {
        setEditingFaq(faq);
        setIsModalVisible(true);
    };

    const handleDeleteFaq = (faqId) => {
        dispatch(deleteCourseFaq(effectiveId, faqId));
    };

    const handleToggleActive = (faq) => {
        const newStatus = faq.is_active === false ? true : false;
        dispatch(updateCourseFaq(effectiveId, faq._id, { is_active: newStatus }));
    };

    const handleModalSubmit = (values) => {
        if (!isHexId(effectiveId)) {
            console.error("Cannot create/update FAQ: Invalid Course ID", effectiveId);
            return;
        }

        if (editingFaq?.isDraft) {
            // Update a local draft
            setDraftFaqs(prev => prev.map(draft => 
                draft._draftId === editingFaq._draftId 
                    ? { ...draft, ...values } 
                    : draft
            ));
        } else if (editingFaq) {
            // Update an existing saved FAQ
            dispatch(updateCourseFaq(effectiveId, editingFaq._id, values));
        } else {
            // Create a brand new FAQ entirely manually
            dispatch(createCourseFaq(effectiveId, values));
        }
        setIsModalVisible(false);
    };

    const handleAIGenerateSuccess = async (generatedFaqs) => {
        if (!Array.isArray(generatedFaqs)) {
            message.error("Invalid AI response format. Expected an array of FAQs.");
            return;
        }
        
        let successCount = 0;
        const newDrafts = [];
        for (const faq of generatedFaqs) {
            if (faq.question && faq.answer) {
                 newDrafts.push({
                     ...faq,
                     _draftId: `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                     isDraft: true,
                     is_active: true,
                     createdAt: new Date().toISOString()
                 });
                 successCount++;
            }
        }
        
        if (successCount > 0) {
            setDraftFaqs(prev => [...newDrafts, ...prev]);
            message.success(`${successCount} generated FAQs have been added as drafts for your review.`);
        }
    };

    const handleSaveDraft = (draft) => {
        if (!isHexId(effectiveId)) return;
        const { question, answer } = draft;
        
        // Dispatch to actual backend
        dispatch(createCourseFaq(effectiveId, { question, answer }));
        
        // Remove from local drafts
        setDraftFaqs(prev => prev.filter(d => d._draftId !== draft._draftId));
        message.success("Draft FAQ successfully saved to database!");
    };



    const handleRegenerateDraft = async (draft) => {
        if (!token) return message.error("Must be logged in to regenerate");
        setRegeneratingId(draft._draftId);
        
        try {
            const configReq = await axios.get(`${appConfig.apiURL}/v2/admin/ai/config?category=FAQ`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const configData = configReq.data?.status && configReq.data?.results ? configReq.data.results : {};
            
            const model = configData.model || "@cf/meta/llama-3-8b-instruct";
            const customPrompt = configData.systemPrompt || "You are a helpful assistant for discovering courses. Generate high-quality SEO-optimized FAQs.";
            
            const messages = [
                { role: "system", content: `${customPrompt}\n\nSTRICT INSTRUCTIONS:\nGenerate exactly ONE frequently asked question and answer for the given course. It must be different from: "${draft.question}". Output STRICTLY as a JSON object with keys 'question' and 'answer'. Do not use markdown blocks around the JSON.` },
                { role: "user", content: `Generate content for course context: Course Title: ${courseInfo?.title || 'Unknown Course'}. Description: ${courseInfo?.description || 'N/A'}` }
            ];

            const response = await axios.post(`${appConfig.apiURL}/v2/ai/generate`, {
                messages,
                modelOverride: model
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = response.data;
            if (!data.status) throw new Error(data.message || "Failed to generate AI content");

            const rawText = data.results.result.response;
            let cleanedText = rawText.trim();
            const jsonStart = Math.min(
                cleanedText.indexOf("[") !== -1 ? cleanedText.indexOf("[") : Infinity,
                cleanedText.indexOf("{") !== -1 ? cleanedText.indexOf("{") : Infinity
            );
            const jsonEnd = Math.max(cleanedText.lastIndexOf("]"), cleanedText.lastIndexOf("}"));
            
            if (jsonStart !== Infinity && jsonEnd !== -1 && jsonEnd > jsonStart) {
                cleanedText = cleanedText.substring(jsonStart, jsonEnd + 1);
            }

            const parsedJson = JSON.parse(cleanedText);
            // If it returns an array by mistake, take the first element
            const newFaq = Array.isArray(parsedJson) ? parsedJson[0] : parsedJson;

            if (newFaq && newFaq.question && newFaq.answer) {
                 setDraftFaqs(prev => prev.map(d => 
                     d._draftId === draft._draftId 
                        ? { ...d, question: newFaq.question, answer: newFaq.answer }
                        : d
                 ));
                 message.success("Draft regenerated successfully");
            } else {
                 throw new Error("Invalid format returned");
            }
        } catch (error) {
            console.error("AI Regeneration Error:", error);
            message.error("Failed to regenerate this FAQ. Please try again.");
        } finally {
            setRegeneratingId(null);
        }
    };

    // Combine Saved FAQs and Drafts for display, ensuring newest saved items are at the top
    // Note: Assuming courseFaqs are fetched in order, but we reverse them or sort by createdAt/updatedAt if available.
    // If backend doesn't sort descending, we can force it here. Let's assume newer items have newer _id hex values or createdAt.
    const sortedCourseFaqs = [...courseFaqs].sort((a, b) => {
        // If they have createdAt, sort descending
        if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt) - new Date(a.createdAt);
        }
        // Fallback: MongoDB _id contains timestamp in first 4 bytes, so localeCompare on hex string works for chronological sorting
        return String(b._id).localeCompare(String(a._id));
    });

    const combinedData = [...draftFaqs, ...sortedCourseFaqs];

    const columns = [
        {
            title: "Question",
            dataIndex: "question",
            key: "question",
            width: "40%",
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                    <div>{text}</div>
                </div>
            ),
        },
        {
            title: "Answer",
            dataIndex: "answer",
            key: "answer",
            width: "40%",
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                    {text}
                </div>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: "220px",
            align: "center",
            render: (_, record) => {
                const isActive = record.is_active !== false;
                
                if (record.isDraft) {
                    return (
                        <div style={{ 
                            display: "flex", 
                            flexDirection: "column", 
                            gap: "8px", 
                            alignItems: "center",
                            width: "140px",
                            margin: "0 auto"
                        }}>
                             <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                                <Tag color="magenta" style={{ margin: 0, fontSize: '10px', lineHeight: '16px' }}>AI DRAFT</Tag>
                                <Tooltip title="Discard Draft">
                                    <Button 
                                        type="text" 
                                        danger 
                                        size="small" 
                                        icon={<CloseCircleOutlined />} 
                                        onClick={() => setDraftFaqs(prev => prev.filter(d => d._draftId !== record._draftId))}
                                        style={{ padding: 0, height: 'auto' }}
                                    />
                                </Tooltip>
                            </div>
                            <div style={{ display: "flex", width: "100%", gap: "6px", alignItems: "center" }}>
                                <Button
                                    size="small"
                                    icon={<SyncOutlined spin={regeneratingId === record._draftId} />}
                                    onClick={() => handleRegenerateDraft(record)}
                                    disabled={regeneratingId === record._draftId}
                                    style={{ 
                                        flex: 1,
                                        fontSize: 10,
                                        fontWeight: 600,
                                        color: "#eb2f96",
                                        borderColor: "#ffadd2",
                                        backgroundColor: "#fff0f6",
                                        borderRadius: 4,
                                        height: "24px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}
                                >
                                    Regenerate
                                </Button>
                                <Tooltip title="Edit Content">
                                    <Button
                                        icon={<EditOutlined />}
                                        onClick={() => handleEditFaq(record)}
                                        size="small"
                                        style={{ 
                                            width: "32px",
                                            height: "24px",
                                            color: "#1890ff", 
                                            borderColor: "#91d5ff",
                                            borderRadius: 4,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}
                                    />
                                </Tooltip>
                            </div>
                            <Button 
                                type="primary"
                                size="small"
                                icon={<CheckOutlined />}
                                onClick={() => handleSaveDraft(record)}
                                style={{ 
                                    width: "100%",
                                    background: "linear-gradient(135deg, #52c41a, #389e0d)", 
                                    borderColor: "transparent",
                                    borderRadius: 4,
                                    fontSize: 12,
                                    height: "30px",
                                    fontWeight: 500
                                }}
                            >
                                Save to database
                            </Button>
                        </div>
                    );
                }

                return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "16px" }}>
                        <Space size={6}>
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => handleEditFaq(record)}
                                type="text"
                                style={{ color: "#1890ff", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center" }}
                            />
                            <Popconfirm
                                title="Are you sure to delete this FAQ?"
                                onConfirm={() => handleDeleteFaq(record._id)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button
                                    icon={<DeleteOutlined />}
                                    type="text"
                                    danger
                                    style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center" }}
                                />
                            </Popconfirm>
                        </Space>
                        <Switch
                            checked={isActive}
                            onChange={() => handleToggleActive(record)}
                            size="small"
                            className={isActive ? "faq-switch-active" : ""}
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <div className="card">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h4 className="card-title" style={{ marginBottom: "4px" }}>Course FAQs</h4>
                        {(courseFaqs.length > 0 || draftFaqs.length > 0) && (
                            <small style={{ color: "#888" }}>
                                <span style={{ color: "#52c41a", fontWeight: 500 }}>{liveCount} Live</span>
                                {" · "}
                                <span style={{ color: hiddenCount > 0 ? "#ff4d4f" : "#888", fontWeight: 500 }}>
                                    {hiddenCount} Hidden
                                </span>
                                {draftFaqs.length > 0 && (
                                    <>
                                        {" · "}
                                        <span style={{ color: "#eb2f96", fontWeight: 500 }}>{draftFaqs.length} Drafts</span>
                                    </>
                                )}
                            </small>
                        )}
                    </div>
                    <Space>
                        <AIGenerateAction 
                            onGenerateSuccess={handleAIGenerateSuccess}
                            contextInstructions="Generate exactly 5 frequently asked questions and answers for the given course. Output STRICTLY as a JSON array of objects with keys 'question' and 'answer'."
                            systemPromptDefault="You are a helpful assistant for discovering courses. Generate high-quality SEO-optimized FAQs."
                            buttonText="Generate FAQs"
                            courseId={effectiveId}
                        />
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddFaq}>
                            Add FAQ
                        </Button>
                    </Space>
                </div>

                {/* Draft FAQs Banner with bulk actions */}
                {draftFaqs.length > 0 && (
                    <div style={{
                        background: "linear-gradient(135deg, #fff0f6 0%, #f9f0ff 100%)",
                        border: "1px solid #f5c6d0",
                        borderRadius: 8,
                        padding: "10px 16px",
                        marginBottom: 16,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                    }}>
                        <Space>
                            <span style={{ fontSize: 16 }}>✨</span>
                            <span style={{ fontWeight: 600, color: "#722ed1" }}>
                                {draftFaqs.length} AI-Generated Draft{draftFaqs.length > 1 ? 's' : ''}
                            </span>
                            <span style={{ color: "#8c8c8c", fontSize: 12 }}>
                                Review, edit, and save the ones you like
                            </span>
                        </Space>
                        <Space size={8}>
                            <Button 
                                size="small"
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={() => {
                                    draftFaqs.forEach(d => handleSaveDraft(d));
                                }}
                                style={{ 
                                    background: "linear-gradient(135deg, #52c41a, #389e0d)",
                                    borderColor: "transparent",
                                    borderRadius: 4
                                }}
                            >
                                Save All
                            </Button>
                            <Popconfirm
                                title={`Discard all ${draftFaqs.length} drafts?`}
                                onConfirm={() => setDraftFaqs([])}
                                okText="Discard All"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                            >
                                <Button 
                                    size="small" 
                                    danger
                                    icon={<CloseCircleOutlined />}
                                    style={{ borderRadius: 4 }}
                                >
                                    Discard All
                                </Button>
                            </Popconfirm>
                        </Space>
                    </div>
                )}
                <Table
                    columns={columns}
                    dataSource={combinedData}
                    rowKey={(record) => record.isDraft ? record._draftId : record._id}
                    pagination={{ pageSize: 15 }}
                    rowClassName={(record) => {
                        if (record.isDraft) return "faq-row-draft";
                        if (record.is_active === false) return "faq-row-hidden";
                        return "";
                    }}
                />
                <CourseFAQForm
                    visible={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    onSubmit={handleModalSubmit}
                    initialValues={editingFaq}
                />
            </div>

            {/* Inline style for hidden FAQ rows and Drafts */}
            <style>{`
                .faq-row-hidden {
                    opacity: 0.5;
                    background-color: #fafafa !important;
                }
                .faq-row-hidden:hover {
                    opacity: 0.75;
                }
                .faq-row-draft td {
                    background: linear-gradient(135deg, #fff0f6 0%, #f9f0ff 100%) !important;
                }
                .faq-row-draft td:first-child {
                    border-left: 3px solid #eb2f96 !important;
                }
                .faq-row-draft:hover td {
                    background: linear-gradient(135deg, #ffe8f0 0%, #f0e5ff 100%) !important;
                }
                .faq-switch-active.ant-switch-checked {
                    background-color: #52c41a !important;
                }
            `}</style>
        </div>
    );
};

export default CourseFAQList;
